import { object, string, number } from 'yup';
import { useLanguage } from '~/Hooks';

export const useLocationValidationScheme = () => {
  const { t } = useLanguage();

  const getMessage = (field: string) => t('form.field.message.required', { field });

  const locationZipKey = 'form.field.location.zip';

  const label = {
    general: t('app.title.general'),
    details: t('app.title.details'),
    zipCode: t(locationZipKey),
    countryISOA2: t('form.field.location.country'),
    street: t('form.field.location.street'),
    city: t('form.field.location.city'),
    name: t('form.field.name'),
    latitude: t(locationZipKey),
    longitude: t(locationZipKey),
    description: t('form.field.location.description'),
    importantInformation: t('form.field.location.importantInfo'),
    website: t('app.website'),
    settings: t('app.settings'),
    defaultCancellationWindow: t('app.defaultCancellationWindow.cancellationDeadline'),
  };

  const error = {
    required: {
      zipCode: getMessage(`${label.general} / ${label.zipCode}`),
      countryISOA2: getMessage(`${label.general} / ${label.countryISOA2}`),
      street: getMessage(`${label.general} / ${label.street}`),
      city: getMessage(`${label.general} / ${label.city}`),
      name: getMessage(`${label.general} / ${label.name}`),
      latitude: getMessage(`${label.general} / ${label.latitude}`),
      longitude: getMessage(`${label.general} / ${label.longitude}`),
      description: getMessage(`${label.details} / ${label.description}`),
      importantInformation: getMessage(`${label.details} / ${label.importantInformation}`),
    },
    invalid: {
      website: `<b>${label.general} / ${label.website}</b> ${t('app.location.website.validation')}`,
      defaultCancellationWindow: `<b>${label.settings} / ${label.defaultCancellationWindow}</b> ${t(
        'app.defaultCancellationWindow.rangeValidationError',
      )}`,
    },
  };

  const validationSchema = object({
    zipCode: string().required(error.required.zipCode),
    countryISOA2: string().required(error.required.countryISOA2),
    street: string().required(error.required.street),
    city: string().required(error.required.city),
    name: string().required(error.required.name),
    latitude: number().required(error.required.latitude),
    longitude: number().required(error.required.longitude),
    description: string().required(error.required.description),
    importantInformation: string().required(error.required.importantInformation),
    website: string()
      .nullable()
      .notRequired()
      .matches(
        /^https:\/\/[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/.*)?$/,
        error.invalid.website, // Custom error message
      ),
    defaultCancellationWindow: number()
      .nullable()
      .min(1, error.invalid.defaultCancellationWindow)
      .max(12, error.invalid.defaultCancellationWindow),
  });

  return {
    validationSchema,
  };
};
